import React, { useEffect, useState } from 'react';
import { FaBook } from 'react-icons/fa';
import fetchReviewers from '../../services/fetchReviewers';
import ReviewerCard from './ReviewerCard';

const Reviewers = () => {
  // State for fetching reviewers
  const [reviewers, setReviewers] = useState([]);
  const [error, setError] = useState(null);

  // State for filter selections
  const [filterOptions, setFilterOptions] = useState({});
  const [courseCode, setCourseCode] = useState('');
  const [quizNumber, setQuizNumber] = useState('');
  const [academicYear, setAcademicYear] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 8;

  const loadData = async () => {
    try {
      // Construct the query string with selected values
      const paramsMap = {
        page: currentPage,
        noPerPage: itemsPerPage
      };

      if (courseCode !== '') {
        paramsMap['data.short_name'] = courseCode;
        if (quizNumber !== '') paramsMap['data.quiz_num'] = quizNumber;
      }
      else setQuizNumber('')

      if (academicYear !== '') paramsMap['data.acad_year'] = academicYear;

      const queryParams = new URLSearchParams(paramsMap).toString();

      // Retrieve select options for filter
      const filterURL = `https://website-api-ten.vercel.app/getFilters`;
      const filters = await fetchReviewers(filterURL);
      setFilterOptions(filters);

      // Modify the URL to include query parameters
      const url = `https://website-api-ten.vercel.app/reviewers?${queryParams}`;
      const data = await fetchReviewers(url);
      setReviewers(data.results);
      setTotalPages(data.pagination.totalPages);

    } catch (err) {
      setError('Failed to load reviewers. Please try again later.');
    }
  };

  useEffect(() => {
    loadData();
  }, [courseCode, quizNumber, academicYear, currentPage]); // Added currentPage as dependency

  // Function to handle the select changes
  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setCurrentPage(1); // Reset to first page when filters change
    if (name === 'courseCode') {
      setCourseCode(value);
    } else if (name === 'quizNumber') {
      setQuizNumber(value);
    } else if (name === 'academicYear') {
      setAcademicYear(value);
    }
  };

  // Function to handle page changes
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className="p-6">
      {/* Header Section */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-800 mt-4 font-header">Academic Resources</h1>
        <p className="text-gray-500 mt-2 font-body">
          SYNTAX offers free reviewers and tutorials for general engineering courses to the GCOE community.
        </p>
        <hr className="border-gray-300 mt-6 w-1/3 mx-auto" />
      </div>

      {/* Reviewers Section */}
      <div className="flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0 mb-6">
        <div className="flex items-center space-x-4">
          <div className="bg-black p-4 rounded-full">
            <FaBook size={30} color="#FFF" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 font-header">Reviewers</h2>
        </div>

        {/* Contact Us Button */}
        <div className="flex space-x-2 justify-center">
          <a className="inline-flex text-center bg-blue-800 text-white px-4 py-2 rounded-md hover:bg-blue-900 font-body" target="_blank" href="https://bit.ly/SyntaxReviewerCopyRequest">
            Request Physical Copy
          </a>
          <a className="inline-flex text-center  bg-blue-800 text-white px-4 py-2 rounded-md hover:bg-blue-900 font-body" target="_blank" href="https://bit.ly/SYNTAX-ReviewerFeedbackForm">
            Contact Us
          </a>
        </div>
      </div>

      {/* Search Filters */}
      <div className="flex space-x-4 mb-6">
        <select
          className="border border-gray-300 rounded-md p-2 bg-white text-gray-800 w-full font-body"
          name="courseCode"
          value={courseCode}
          onChange={handleSelectChange}
        >
          <option key="" value="">
              Course Code
          </option>
          {filterOptions.short_name && filterOptions.short_name.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        <select
          className="border border-gray-300 rounded-md p-2 bg-white text-gray-800 w-full font-body"
          name="quizNumber"
          value={quizNumber}
          onChange={handleSelectChange}
        >
          <option key="" value="">
              Coverage
          </option>
          {filterOptions.quiz_num && courseCode !== '' && filterOptions.quiz_num[courseCode].map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        <select
          className="border border-gray-300 rounded-md p-2 bg-white text-gray-800 w-full font-body"
          name="academicYear"
          value={academicYear}
          onChange={handleSelectChange}
        >
          <option key="" value="">
              Academic Year
          </option>
          {filterOptions.acad_year && filterOptions.acad_year.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      {/* List of Reviewers */}
      <div className="flex flex-wrap justify-center gap-6 mb-6">
        {reviewers?.length > 0 ? (
          reviewers.map((card, index) => (
            <ReviewerCard key={index} card={card} />
          ))
        ) : (
          <p>No reviewers found based on the selected filters.</p>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-4 mb-6">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-md font-body ${
              currentPage === 1
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-blue-700 hover:bg-blue-800 text-white'
            }`}
          >
            Previous
          </button>
          
          <div className="flex space-x-2">
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index + 1}
                onClick={() => handlePageChange(index + 1)}
                className={`px-4 py-2 rounded-md font-body ${
                  currentPage === index + 1
                    ? 'bg-blue-800 text-white'
                    : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded-md font-body ${
              currentPage === totalPages
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-blue-700 hover:bg-blue-800 text-white'
            }`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Reviewers;
